/* eslint-disable no-console */
import { useContext } from 'react';
import { usePersistFn } from 'ahooks';
import { useIntl, useDispatch, useSelector } from 'umi';
import type { FosterFormModelsState, FosterStepModelsState, UserState } from 'umi';
import { stepInfo, StepType } from '@/utils/FosterServiceConf';
import { history } from '@@/core/history';
import * as queryString from 'query-string';
import { commonPostRequest } from '@/utils/apiUtils';
import { Modal } from 'antd';
import StepContext from './StepContext';
import { isMobile } from '@/utils';
import { securityLevel } from '@/utils/ServiceConf';

const submitApply = async (params?: Record<string, unknown>): Promise<unknown> => commonPostRequest<unknown>('/api/adoptappli/apply', params);

function useHandleStep(currentStep: { stepType: StepType; stepName: string }) {
  const context = useContext(StepContext);
  const dispatch = useDispatch();
  const { profile } = useSelector(({ user }: { user: UserState }) => user);
  const { step } = useSelector((state: { fosterStepModels: FosterStepModelsState }) => state.fosterStepModels);
  // eslint-disable-next-line max-len
  const { formData } = useSelector(({ fosterFormModels }: { fosterFormModels: FosterFormModelsState }) => fosterFormModels);
  const intl = useIntl();
  // 更新/初始化models
  const updataModels = usePersistFn((payload: unknown) => {
    dispatch({
      type: 'fosterStepModels/update',
      payload,
    });
  });


  /**
   * 最後一步提交操作
   * status: 0
   */
  const _handleSaveOrSubmit = usePersistFn<() => void>(async () => {
    console.log(formData);
    console.log(profile);
    const localeMap = {
      'zh-MO': 1,
      'pt-PT': 2,
      'pt-US': 3,
    };
    // 构建提交对象（使用驼峰命名）
    const appliData = {
      // 优先取nameCn（去掉逗号），如果nameCn为空则取namePt（替换逗号为空格）
       applicantName: profile.nameCn
        ? profile.nameCn.replace(',', '')
        : profile.namePt?.replace(',', ' '),
      // nameCn去掉逗号
      applicantNameCn: profile.nameCn?.replace(',', ''),
      // nameCn的姓（逗号前内容）
      applicantLastNameCn: profile.nameCn?.split(',')[0],
      // nameCn的名（逗号后内容）
      applicantFirstNameCn: profile.nameCn?.includes(',')
        ? profile.nameCn.split(',')[1]?.trim()
        : profile.nameCn,
      // namePt替换逗号为空格
      applicantNameEn: profile.namePt?.replace(',', ' '),
      // namePt的姓（逗号前内容）
      applicantLastNameEn: profile.namePt?.split(',')[0],
      // namePt的名（逗号后内容）
      applicantFirstNameEn: profile.namePt?.includes(',')
        ? profile.namePt.split(',')[1]?.trim()
        : profile.namePt,
      // gender转换（M=1, F=0）
      applicantGender: profile.gender === 'M' ? 0 : 1,
      // 配偶姓名来自表单
      spouseName: formData.spouseName,
      // 手机号来自表单
      applicantCellphone: formData.phone?.number,
      applicantAreano: formData.phone?.area,
      // 地址来自表单
      applicantAddress: formData.addressObj?.fullAddr,
      // 页面语言（需要根据实际获取语言方式调整）
      applicantNotifyLanguage: localeMap[intl.locale] || 1, // 默认为1,
      // euid
      euid: profile.euid,
    };
    try {
      submitApply(appliData)
        .then((data) => {
          if (data) {
            console.log(data);
            history.push(
              queryString.stringifyUrl({
                url: '/foster-complete-submission',
                query: {
                  status:data.success,
                  msg: data.msg,
                  submitTime: data.name,
                },
              }),
            );
          } else {
            console.error(data);
            let errorMsg = data.msg;
            if (localeMap[intl.locale] === 2) {
              errorMsg = data.msgPt;
            } else if (localeMap[intl.locale] === 3) {
              errorMsg = data.msgEn;
            }
            Modal.error({
              title: intl.formatMessage({ id: '提示' }),
              content: intl.formatMessage({ id: errorMsg }),
            });
          }
        })
        .catch((error) => {
          console.error('获取列表失败:', error);
        });
    } catch (error) {
      console.error('网络错误:', error);
      // 在这里处理网络错误
    }
  });

  /**
   * 下一步跳转
   */
  const _handleNext = () => {
    // 最后一步，提交，后续也需根据需求区分是否有提交不在最后一步的情况情况
    console.log('_handleNext');
    console.log(step, stepInfo.length);
    if (step === stepInfo.length) {
      // console.log('最後一步。。。。');
      if (isMobile()) {
        context.identityRef.handleAuth({ authLevel: securityLevel.HIGH }, profile.euid);
      } else {
        _handleSaveOrSubmit();
      }
    } else {
      // 更新當前步驟
      updataModels({ step: step + 1 });
    }
  };

  /**
   * 下一步业务封装
   */
  const handleNextStep = usePersistFn(async () => {
    // CASE2: 表单类步骤触发校验并保存
    if (currentStep.stepType === StepType.form) {
      const formValidateFields = context.formRenderInstance?.validateFields;
      const formValue = await formValidateFields?.();
      // 将数据保存到model中
      dispatch({
        type: 'fosterFormModels/updateFormData',
        payload: formValue,
      });
      // 跳转到下一步
      _handleNext();
      return;
    }
    // CASE3: 身份识别步骤特殊处理
    if (currentStep.stepType === StepType.faceRecognition && isMobile()) {
      // 如果尚未进行身份识别，则触发身份识别
      if (!context.isAuth && context.identityRef?.handleAuth) {
        // 调用身份识别方法
        context.identityRef.handleAuth({ authLevel: securityLevel.HIGH }, profile.euid);
        return; // 不跳转到下一步，等待身份识别完成
      }
      // 如果已完成身份识别，则跳转到下一步
      _handleSaveOrSubmit();
      return;
    }
    // CASE5: 其他直接跳转到下一步
    _handleNext();
  });

  return handleNextStep;
}

export default useHandleStep;
